import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlarmClock, X } from "lucide-react";
import { useAccessToken } from "@/auth/useAccessToken";
import { pushDesktopNotification } from "@/lib/notifications";

const mailApiBase = import.meta.env.VITE_MAIL_BFF_BASE_URL ?? "";
const MINUTES_KEY = "athavita:mail:reminder-minutes";
const FIRED_KEY = "athavita:mail:reminders-fired";
const DEFAULT_MINUTES = 15;

/** Lead times offered in Settings; 0 turns reminders off. */
export const REMINDER_CHOICES = [0, 5, 10, 15, 30, 60] as const;

export function loadReminderMinutes(): number {
    try {
        const raw = localStorage.getItem(MINUTES_KEY);
        if (raw !== null) {
            const v = Number(raw);
            if ((REMINDER_CHOICES as readonly number[]).includes(v)) return v;
        }
    } catch { /* storage unavailable — use the default */ }
    return DEFAULT_MINUTES;
}

export function saveReminderMinutes(minutes: number) {
    try {
        localStorage.setItem(MINUTES_KEY, String(minutes));
    } catch { /* storage disabled — the choice just won't persist */ }
    window.dispatchEvent(new CustomEvent("mail:reminders-changed"));
}

interface Row {
    id: string;
    subject: string;
    startUtc: string;
    isAllDay: boolean;
    location?: string | null;
    status: string;
    response?: string | null;
    recurrenceRule?: string | null;
    recurrenceException?: string | null;
}

interface Due {
    key: string;
    subject: string;
    start: Date;
    location: string | null;
}

/** Syncfusion's RRULE expander: occurrence start times, in ms. */
type Generate = (start: Date, rule: string, exclude: string, firstDayOfWeek: number, maxCount?: number, viewDate?: Date) => number[];

/**
 * Outlook-style reminders, mounted once in the app shell: a desktop
 * notification plus a card in the corner some minutes before each timed event
 * (15 by default, set under Settings › General › Calendar). Repeating events
 * are expanded with the Schedule's own rule engine, loaded only when needed.
 * Each occurrence fires once — remembered across reloads and tabs.
 */
export function EventReminders() {
    const token = useAccessToken();
    const navigate = useNavigate();
    const [minutes, setMinutes] = useState(loadReminderMinutes);
    const [rows, setRows] = useState<Row[]>([]);
    const [due, setDue] = useState<Due[]>([]);
    const [, setTick] = useState(0);
    const generateRef = useRef<Generate | null>(null);

    useEffect(() => {
        const onChange = () => setMinutes(loadReminderMinutes());
        window.addEventListener("mail:reminders-changed", onChange);
        window.addEventListener("storage", onChange);
        return () => {
            window.removeEventListener("mail:reminders-changed", onChange);
            window.removeEventListener("storage", onChange);
        };
    }, []);

    // Events around now, refreshed every five minutes.
    useEffect(() => {
        if (!token || minutes === 0) return;
        let cancelled = false;
        const load = async () => {
            const from = new Date(Date.now() - 86_400_000).toISOString();
            const to = new Date(Date.now() + 2 * 86_400_000).toISOString();
            try {
                const res = await fetch(
                    `${mailApiBase}/api/calendar/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!res.ok || cancelled) return;
                const data = (await res.json()) as Row[];
                if (cancelled || !Array.isArray(data)) return;
                if (!generateRef.current && data.some((r) => r.recurrenceRule)) {
                    const mod = await import("@syncfusion/ej2-schedule");
                    generateRef.current = mod.generate as Generate;
                }
                if (!cancelled) setRows(data);
            } catch { /* keep the last list */ }
        };
        void load();
        const t = window.setInterval(() => void load(), 5 * 60_000);
        return () => { cancelled = true; window.clearInterval(t); };
    }, [token, minutes]);

    // Every 30 s: anything starting within the lead time (or in the last minute) fires once.
    useEffect(() => {
        if (minutes === 0) { setDue([]); return; }
        const check = () => {
            const now = Date.now();
            const fired = loadFired();
            const fresh: Due[] = [];
            for (const row of rows) {
                if (row.isAllDay || row.status === "cancelled" || row.response === "declined") continue;
                for (const t of occurrences(row, now - 60_000, now + minutes * 60_000, generateRef.current)) {
                    const key = `${row.id}:${t}`;
                    if (fired[key]) continue;
                    fired[key] = now;
                    fresh.push({ key, subject: row.subject, start: new Date(t), location: firstLine(row.location) });
                }
            }
            setTick((n) => n + 1);
            if (fresh.length === 0) return;
            saveFired(fired);
            for (const d of fresh) pushDesktopNotification(d.subject, describe(d), { sound: true });
            setDue((prev) => [...prev, ...fresh].slice(-5));
        };
        check();
        const t = window.setInterval(check, 30_000);
        return () => window.clearInterval(t);
    }, [rows, minutes]);

    const dismiss = (key: string) => setDue((prev) => prev.filter((d) => d.key !== key));
    const snooze = (d: Due) => {
        dismiss(d.key);
        window.setTimeout(() => {
            pushDesktopNotification(d.subject, describe(d), { sound: true });
            setDue((prev) => [...prev.filter((x) => x.key !== d.key), d]);
        }, 5 * 60_000);
    };

    // Rendered inside the shell's shared "Reminders" corner (task reminders stack there too).
    if (due.length === 0) return null;
    return (
        <>
            {due.map((d) => (
                <div key={d.key} role="alert" className="pointer-events-auto rounded-lg border bg-card p-3 shadow-lg">
                    <div className="flex items-start gap-2">
                        <AlarmClock className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold">{d.subject}</p>
                            <p className="text-xs text-muted-foreground">{describe(d)}</p>
                        </div>
                        <button
                            type="button"
                            aria-label="Dismiss reminder"
                            onClick={() => dismiss(d.key)}
                            className="rounded p-0.5 hover:bg-accent"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </div>
                    <div className="mt-2 flex justify-end gap-2">
                        <button type="button" onClick={() => snooze(d)} className="h-7 rounded-md border px-2 text-xs hover:bg-accent">
                            Snooze 5 min
                        </button>
                        <button
                            type="button"
                            onClick={() => { dismiss(d.key); navigate("/calendar"); }}
                            className="h-7 rounded-md bg-primary px-2 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                        >
                            Open calendar
                        </button>
                    </div>
                </div>
            ))}
        </>
    );
}

/** Occurrence start times of one row inside [from, to]. */
function occurrences(row: Row, from: number, to: number, generate: Generate | null): number[] {
    const start = new Date(row.startUtc).getTime();
    if (!row.recurrenceRule) return start >= from && start <= to ? [start] : [];
    if (!generate || start > to) return [];
    try {
        return generate(new Date(row.startUtc), row.recurrenceRule, row.recurrenceException ?? "", 1, 400, new Date(from - 86_400_000))
            .filter((t) => t >= from && t <= to);
    } catch {
        return [];
    }
}

function describe(d: Due): string {
    const mins = Math.round((d.start.getTime() - Date.now()) / 60_000);
    const when = mins > 0 ? `in ${mins} min` : mins === 0 ? "now" : `started ${-mins} min ago`;
    const time = d.start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return `${time} (${when})${d.location ? ` · ${d.location}` : ""}`;
}

/** Teams invites put a whole join block in Location — keep its first line. */
function firstLine(s: string | null | undefined): string | null {
    const line = s?.split(/\r?\n/)[0]?.trim();
    return line ? line.slice(0, 80) : null;
}

function loadFired(): Record<string, number> {
    try {
        return JSON.parse(localStorage.getItem(FIRED_KEY) ?? "{}") as Record<string, number>;
    } catch {
        return {};
    }
}

function saveFired(map: Record<string, number>) {
    const cutoff = Date.now() - 2 * 86_400_000;
    const kept = Object.fromEntries(Object.entries(map).filter(([, t]) => t > cutoff));
    try {
        localStorage.setItem(FIRED_KEY, JSON.stringify(kept));
    } catch { /* storage full or disabled — worst case a reminder repeats */ }
}
